import { useState, useEffect } from 'react';
import { Modal } from '@components/common';
import { getJobDocuments, type JobDocument } from '@lib/api/jobs';
import { linkDocumentToInvoice, type InvoiceAttachment } from '@lib/api/invoices';

interface JobDocumentPickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  jobId: string;
  invoiceId: number;
  existingAttachments: InvoiceAttachment[];
  onDocumentsLinked: () => void;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function isImageType(type: string): boolean {
  return type.startsWith('image/');
}

export const JobDocumentPickerModal: React.FC<JobDocumentPickerModalProps> = ({
  isOpen,
  onClose,
  jobId,
  invoiceId,
  existingAttachments,
  onDocumentsLinked,
}) => {
  const [documents, setDocuments] = useState<JobDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [linking, setLinking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // IDs of documents already linked to this invoice
  const linkedDocIds = new Set(
    existingAttachments
      .filter(a => a.document_id != null)
      .map(a => a.document_id!)
  );

  useEffect(() => {
    if (isOpen) {
      setLoading(true);
      setSelected(new Set());
      setError(null);
      getJobDocuments(jobId)
        .then(setDocuments)
        .catch(() => setError('Failed to load job documents'))
        .finally(() => setLoading(false));
    }
  }, [isOpen, jobId]);

  const toggleSelect = (docId: number) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(docId)) {
        next.delete(docId);
      } else {
        next.add(docId);
      }
      return next;
    });
  };

  const handleLink = async () => {
    setLinking(true);
    setError(null);
    try {
      for (const docId of selected) {
        await linkDocumentToInvoice(invoiceId, docId);
      }
      onDocumentsLinked();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to link documents');
    } finally {
      setLinking(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Add from Job Documents"
      size="lg"
      footer={
        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-gray-600 text-gray-300 hover:bg-gray-700 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleLink}
            disabled={selected.size === 0 || linking}
            className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {linking ? 'Linking...' : `Add ${selected.size} Document${selected.size !== 1 ? 's' : ''}`}
          </button>
        </div>
      }
    >
      {error && (
        <div className="mb-3 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12">
          <span className="material-symbols-outlined animate-spin text-3xl text-gray-400">progress_activity</span>
        </div>
      ) : documents.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <span className="material-symbols-outlined text-4xl mb-2 opacity-50 block">folder_off</span>
          <p>No documents found for this job</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 max-h-[60vh] overflow-auto">
          {documents.map((doc) => {
            const alreadyLinked = linkedDocIds.has(doc.id);
            const isSelected = selected.has(doc.id);

            return (
              <button
                key={doc.id}
                onClick={() => !alreadyLinked && toggleSelect(doc.id)}
                disabled={alreadyLinked}
                className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                  alreadyLinked
                    ? 'border-gray-600 opacity-50 cursor-not-allowed'
                    : isSelected
                    ? 'border-blue-500 ring-2 ring-blue-500/30'
                    : 'border-gray-700 hover:border-gray-500 cursor-pointer'
                }`}
              >
                <div className="w-full h-full bg-gray-800 flex items-center justify-center">
                  {isImageType(doc.type) ? (
                    <img src={doc.url} alt={doc.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="flex flex-col items-center text-gray-400">
                      <span className="material-symbols-outlined text-4xl">
                        {doc.type === 'application/pdf' ? 'picture_as_pdf' : 'description'}
                      </span>
                      <span className="text-xs mt-1 uppercase">{doc.name.split('.').pop()}</span>
                    </div>
                  )}
                </div>

                {/* Selection indicator */}
                {isSelected && (
                  <div className="absolute top-2 right-2 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                    <span className="material-symbols-outlined text-white text-sm">check</span>
                  </div>
                )}

                {alreadyLinked && (
                  <div className="absolute top-2 right-2 px-2 py-0.5 bg-gray-700 rounded text-xs text-gray-300">
                    Linked
                  </div>
                )}

                {/* File name */}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2">
                  <p className="text-xs text-white truncate">{doc.name}</p>
                  <p className="text-[10px] text-gray-300">{formatFileSize(doc.size)}</p>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </Modal>
  );
};
